import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ProgressStep } from '../components/ui/ProgressStep';
import { ObjectIdPill } from '../components/ui/ObjectIdPill';
import { ArrowRightIcon } from 'lucide-react';
import { getWizardState, getRecommendation } from '../lib/wizardUtils';
import type { StatusType } from '../components/ui/StatusBadge';
export function ProvisioningProgressPage() {
  const wizardState = useMemo(() => getWizardState(), []);
  const rec = useMemo(() => getRecommendation(wizardState), [wizardState]);
  // Simulate progress: steps complete one by one
  const [completedSteps, setCompletedSteps] = useState(0);
  const totalSteps = rec.provisioningSteps.length;
  useEffect(() => {
    if (completedSteps < totalSteps - 1) {
      const timer = setTimeout(() => {
        setCompletedSteps(prev => prev + 1);
      }, 1200 + Math.random() * 800);
      return () => clearTimeout(timer);
    }
  }, [completedSteps, totalSteps]);
  const progress = Math.round((completedSteps + 0.5) / totalSteps * 100);
  const allDone = completedSteps >= totalSteps - 1;
  // Generate mock object IDs for each step's details
  const stepDetails = useMemo(() => {
    const details: Record<number, React.ReactNode> = {};
    rec.provisioningSteps.forEach((step, i) => {
      if (step.label.includes('Verify Stripe')) {
        details[i] = <div className="text-sm text-muted-foreground" data-id="element-1207">
            Connected to account{' '}
            <ObjectIdPill id={wizardState.stripeAccountId || 'acct_PK' + Math.random().toString(36).substring(2, 10).toUpperCase()} className="ml-1" data-id="element-1208" />
          </div>;
      } else if (step.label.includes('Create products')) {
        const products = rec.stripeObjects.filter(o => o.type === 'product');
        details[i] = <div className="space-y-2" data-id="element-1209">
            {products.map(p => <div key={p.id} className="flex justify-between items-center" data-id="element-1210">
                <span className="text-sm" data-id="element-1211">{p.name}</span>
                <ObjectIdPill id={p.id} data-id="element-1212" />
              </div>)}
          </div>;
      } else if (step.label.includes('Create prices')) {
        const prices = rec.stripeObjects.filter(o => o.type === 'price');
        details[i] = <div className="space-y-2" data-id="element-1213">
            {prices.map(p => <div key={p.id} className="flex justify-between items-center" data-id="element-1214">
                <span className="text-sm" data-id="element-1215">{p.name}</span>
                <ObjectIdPill id={p.id} data-id="element-1216" />
              </div>)}
          </div>;
      } else if (step.label.includes('Customer Portal')) {
        const portal = rec.stripeObjects.find(o => o.type === 'portal');
        if (portal) {
          details[i] = <div className="text-sm text-muted-foreground" data-id="element-1217">
              Portal config: <ObjectIdPill id={portal.id} className="ml-1" data-id="element-1218" />
            </div>;
        }
      } else if (step.label.includes('webhook')) {
        const wh = rec.stripeObjects.find(o => o.type === 'webhook');
        details[i] = <div className="text-sm text-muted-foreground" data-id="element-1219">
            {wh && <div className="mb-1" data-id="element-1220">
                Endpoint: <ObjectIdPill id={wh.id} className="ml-1" data-id="element-1221" />
              </div>}
            Subscribing to {rec.events.length} events...
          </div>;
      } else if (step.label.includes('implementation pack')) {
        details[i] = <div className="text-sm text-muted-foreground" data-id="element-1222">
            Generating {rec.frontendLabel} + {rec.backendLabel} code snippets,{' '}
            {rec.dbLabel} schema, and deployment guides...
          </div>;
      } else if (step.label.includes('Tax')) {
        details[i] = <div className="text-sm text-muted-foreground" data-id="element-1223">
            Enabling automatic tax calculation for all prices
          </div>;
      }
    });
    return details;
  }, [rec, wizardState]);
  const getStepStatus = (index: number): StatusType => {
    if (index < completedSteps) return 'success';
    if (index === completedSteps) return allDone ? 'success' : 'in-progress';
    return 'pending';
  };
  const getTimestamp = (index: number): string | undefined => {
    if (index > completedSteps) return undefined;
    const base = new Date();
    base.setSeconds(base.getSeconds() - (completedSteps - index) * 4);
    return base.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit'
    });
  };
  return <div className="min-h-screen bg-pk-surface py-12 px-4" data-id="element-1224">
      <div className="container mx-auto max-w-2xl" data-id="element-1225">
        <div className="bg-card border border-border rounded-xl p-8 shadow-sm" data-id="element-1226">
          <div className="text-center mb-8" data-id="element-1227">
            <h1 className="text-2xl font-bold mb-2" data-id="element-1228">
              {allDone ? 'Your payments stack is ready!' : 'Setting up your payments stack...'}
            </h1>
            <p className="text-muted-foreground" data-id="element-1229">
              {allDone ? `Successfully configured ${rec.modules.length} modules with ${rec.events.length} webhook events.` : "Please don't close this window. This usually takes about 30 seconds."}
            </p>

            <div className="mt-6 h-2 bg-muted rounded-full overflow-hidden" data-id="element-1230">
              <div className={`h-full transition-all duration-1000 ease-out ${allDone ? 'bg-pk-success' : 'bg-primary'}`} style={{
              width: `${allDone ? 100 : progress}%`
            }} data-id="element-1231" />
            </div>
            <div className="mt-2 text-xs text-muted-foreground" data-id="element-1232">
              {completedSteps + (allDone ? 1 : 0)} of {totalSteps} steps
              complete
            </div>
          </div>

          <div className="pl-4" data-id="element-1233">
            {rec.provisioningSteps.map((step, i) => <ProgressStep key={i} status={getStepStatus(i)} label={step.label} timestamp={getTimestamp(i)} details={i <= completedSteps ? stepDetails[i] : undefined} isLast={i === rec.provisioningSteps.length - 1} data-id="element-1234" />)}
          </div>

          <div className="mt-10 pt-6 border-t border-border flex justify-end" data-id="element-1235">
            <Link to="/setup/complete" className={`px-6 py-2.5 rounded-md font-medium flex items-center gap-2 transition-all ${allDone ? 'bg-primary text-primary-foreground hover:opacity-90' : 'bg-muted text-muted-foreground cursor-not-allowed'}`} onClick={e => !allDone && e.preventDefault()} data-id="element-1236">
              View Implementation Pack
              <ArrowRightIcon className="w-4 h-4" data-id="element-1237" />
            </Link>
          </div>
        </div>
      </div>
    </div>;
}